import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  Search,
  Visibility,
  People,
  PaidOutlined,
  PersonOff,
} from '@mui/icons-material';
import { getCustomers, getCustomerDetail, CustomerFilters } from '../../services/api/partnerService';
import { OEMCustomerListResponse, OEMCustomerDetail } from '../../types';

const formatCurrency = (amount: number): string => {
  return `¥${amount.toLocaleString()}`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'free':
      return 'warning';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'active':
      return '有料';
    case 'free':
      return '無料';
    case 'cancelled':
      return '解約';
    default:
      return status;
  }
};

interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
}

const SummaryCard = ({ title, value, icon, color = '#1976d2' }: SummaryCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor: `${color}15`,
            borderRadius: 2,
            p: 1.5,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const PartnerCustomersPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<OEMCustomerListResponse | null>(null);

  // フィルター
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // 詳細ダイアログ
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<OEMCustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0);

  useEffect(() => {
    loadCustomers();
  }, [page, rowsPerPage]);

  const loadCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: CustomerFilters = {
        search: search || undefined,
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      const data = await getCustomers(filters);
      setCustomers(data);
    } catch (err: any) {
      console.error('Customers load error:', err);
      setError(err.response?.data?.detail || '顧客情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadCustomers();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleOpenDetail = async (customerId: number) => {
    setDetailLoading(true);
    setDetailDialog(true);
    setDetailTab(0);
    try {
      const detail = await getCustomerDetail(customerId);
      setSelectedCustomer(detail);
    } catch (err: any) {
      console.error('Detail load error:', err);
      setError(err.response?.data?.detail || '顧客詳細の取得に失敗しました');
      setDetailDialog(false);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && !customers) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          顧客管理
        </Typography>
        <Typography variant="body2" color="text.secondary">
          あなた経由で登録した顧客の一覧
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* サマリーカード */}
      {customers && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="総顧客数"
              value={customers.total}
              icon={<People sx={{ fontSize: 32, color: '#1976d2' }} />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="有料顧客"
              value={customers.total_paid}
              icon={<PaidOutlined sx={{ fontSize: 32, color: '#2e7d32' }} />}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="無料顧客"
              value={customers.total_free}
              icon={<PersonOff sx={{ fontSize: 32, color: '#ed6c02' }} />}
              color="#ed6c02"
            />
          </Grid>
        </Grid>
      )}

      {/* 検索 */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="メールアドレスまたは名前で検索..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyPress={handleKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button variant="contained" size="small" onClick={handleSearch}>
                  検索
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* 顧客一覧 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>顧客</TableCell>
              <TableCell>商品</TableCell>
              <TableCell>プラン</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell align="right">LTV</TableCell>
              <TableCell>登録日</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers?.items.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell>
                  <Box>
                    <Typography fontWeight="bold">{customer.full_name || '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {customer.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{customer.product_name || '-'}</TableCell>
                <TableCell>
                  {customer.plan_code ? (
                    <Chip label={customer.plan_code} size="small" />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(customer.status)}
                    color={getStatusColor(customer.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">{formatCurrency(customer.ltv)}</TableCell>
                <TableCell>{formatDate(customer.registered_at)}</TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => handleOpenDetail(customer.id)}>
                    <Visibility />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!customers || customers.items.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    顧客がいません
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {customers && customers.total > 0 && (
          <TablePagination
            component="div"
            count={customers.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="表示件数:"
          />
        )}
      </TableContainer>

      {/* 詳細ダイアログ */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          顧客詳細
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedCustomer ? (
            <>
              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2 }}>
                <Tab label="基本情報" />
                <Tab label="取引履歴" />
              </Tabs>

              {detailTab === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">メール</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedCustomer.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">名前</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedCustomer.full_name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">商品</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedCustomer.product_name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">プラン</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedCustomer.plan_code || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">ステータス</Typography>
                    <Chip
                      label={getStatusLabel(selectedCustomer.status)}
                      color={getStatusColor(selectedCustomer.status)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">LTV</Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {formatCurrency(selectedCustomer.ltv)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">支払回数</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedCustomer.payment_count}回</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">登録日</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatDate(selectedCustomer.registered_at)}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {detailTab === 1 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>日時</TableCell>
                        <TableCell>商品</TableCell>
                        <TableCell>プラン</TableCell>
                        <TableCell align="right">金額</TableCell>
                        <TableCell>ステータス</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedCustomer.transactions?.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell>{formatDate(tx.created_at)}</TableCell>
                          <TableCell>{tx.product_name}</TableCell>
                          <TableCell>
                            <Chip label={tx.plan_code} size="small" />
                          </TableCell>
                          <TableCell align="right">
                            {tx.amount === 0 ? (
                              <Chip label="無料" color="success" size="small" />
                            ) : (
                              formatCurrency(tx.amount)
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tx.status}
                              color={tx.status === 'completed' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!selectedCustomer.transactions || selectedCustomer.transactions.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">取引履歴がありません</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerCustomersPage;
